import { RECIPE_ACTIONS } from 'application/Shared/Constants/Workato';
import { debug as logger } from 'application/Shared/Utils';
import axios from 'axios';
import { t } from 'i18next';
import { useCallback } from 'react';

/**
 * Interface for the parameters of the runRecipe function
 */
export interface RunRecipeParams {
    /** The ID of the application */
    applicationId?: string;
    /** The action to run */
    action: RECIPE_ACTIONS;
    /** The ID of the platform tenant */
    platformTenantId: string;
    /** The parameters for the recipe */
    recipeParams?: Record<string, unknown>;
}

/**
 * Interface for the return value of the runRecipe function
 */
interface RunRecipeResult {
    data: RunRecipeResponseData['data'] | null;
    error: string | null;
    workatoError: string | null;
}

interface RunRecipeResponseData {
    data: {
        applicationId: string;
        code: number;
        data?: unknown;
        error?: unknown;
        jobId?: string;
    };
    message: string;
    serverResponse: number;
}

interface RunRecipesResponseData {
    data: RunRecipeResponseData['data'][];
    message: string;
    serverResponse: number;
}

/**
 * Interface for the parameters of the runRecipe function within useRunCustomerRecipe
 */
interface RunSingleRecipeParams {
    /** The ID of the specific recipe to run */
    recipeId: string;
    /** The ID of the platform tenant */
    platformTenantId: string;
    /** Optional parameters for the recipe */
    recipeParams?: Record<string, unknown>;
}

/**
 * Interface for the return value of the useRunCustomerRecipe hook
 */
interface UseRunCustomerRecipeResult {
    /**
     * Function to run a single customer recipe by its ID.
     * @param params - The parameters for running the single recipe.
     * @returns A promise that resolves to the result of running the recipe.
     */
    runRecipe: (params: RunSingleRecipeParams) => Promise<RunRecipeResult>;
}

/**
 * Interface for the return value of the useRunCustomerRecipes hook
 */
interface UseRunCustomerRecipesResult {
    /**
     * Function to run multiple customer recipes based on action and optional application ID.
     * @param params - The parameters for running the recipes.
     * @returns A promise that resolves to an array of results, one for each recipe run.
     */
    runRecipes: (params: RunRecipeParams) => Promise<RunRecipeResult[]>;
}

/**
 * Custom hook for running a single customer recipe by its ID.
 * @returns An object containing the `runRecipe` function.
 */
export function useRunCustomerRecipe(): UseRunCustomerRecipeResult {
    /**
     * Runs a single customer recipe with the given parameters.
     * @param {RunSingleRecipeParams} params - The parameters for running the recipe.
     * @returns {Promise<RunRecipeResult>} A promise that resolves to the result of running the recipe.
     */
    const runRecipe = useCallback(
        async ({
            recipeId,
            platformTenantId,
            recipeParams
        }: RunSingleRecipeParams): Promise<RunRecipeResult> => {
            let data: RunRecipeResponseData['data'] | null = null;
            let error: string | null = null;
            let workatoError: string | null = null;

            if (!recipeId || !platformTenantId) {
                return {
                    data: null,
                    error: t('Error.missingRequiredParameters'),
                    workatoError: null
                };
            }

            try {
                const response = await axios.put<RunRecipeResponseData>(
                    `/workato/embedded/recipe/run/${recipeId}`,
                    {
                        platformTenantId,
                        recipeParams
                    }
                );
                data = response.data.data;
                // eslint-disable-next-line @typescript-eslint/no-explicit-any -- generic error handling
            } catch (err: any) {
                error = err.message;
                if (err.response?.data?.message) {
                    workatoError = err.response.data.message.errorMessage;
                    error = `${error}\n${workatoError}`;
                }
                logger.e('Error running customer recipe.', {
                    error: err,
                    platformTenantId,
                    recipeId
                });
            }

            return {
                data,
                error,
                workatoError
            };
        },
        []
    );

    return { runRecipe };
}

/**
 * Custom hook for running multiple customer recipes based on action and optional application ID.
 * @returns An object containing the `runRecipes` function.
 */
export function useRunCustomerRecipes(): UseRunCustomerRecipesResult {
    /**
     * Runs customer recipes matching the given parameters (action, platformTenantId, etc.).
     * @param {RunRecipeParams} params - The parameters for finding and running the recipes.
     * @returns {Promise<RunRecipeResult[]>} A promise that resolves to an array of results, one for each recipe run.
     */
    const runRecipes = useCallback(
        async ({
            applicationId,
            action,
            platformTenantId,
            recipeParams
        }: RunRecipeParams): Promise<RunRecipeResult[]> => {
            let responseData: RunRecipesResponseData['data'] = [];
            let apiError: string | null = null; // General API call error
            let workatoError: string | null = null;

            if (!action || !platformTenantId) {
                return [
                    {
                        data: null,
                        error: t('Error.missingRequiredParameters'),
                        workatoError: null
                    }
                ];
            }

            try {
                const response = await axios.put<RunRecipesResponseData>(
                    '/workato/embedded/recipe/run',
                    {
                        action,
                        applicationId,
                        platformTenantId,
                        recipeParams
                    }
                );
                responseData = response.data.data || []; // Ensure responseData is an array
                // Note: The top-level response.data.message might indicate overall success/failure,
                // but individual recipe results are in the response.data.data array.
                // We will derive errors from individual recipe results below.
                // eslint-disable-next-line @typescript-eslint/no-explicit-any -- generic error handling
            } catch (err: any) {
                apiError = err.message;
                if (err.response?.data?.message?.length) {
                    workatoError = err.response.data.message[0].errorMessage;
                    apiError = `${apiError}\n${workatoError}`;
                }
                logger.e('Error running customer recipe.', err);
                responseData = [
                    {
                        applicationId: 'app123',
                        code: 500,
                        data: null,
                        error: 'API Error'
                    }
                ];
            }

            // Process results for each recipe run returned by the API
            const results = responseData.map((recipe) => ({
                data: recipe.data,
                error: apiError,
                workatoError
            })) as RunRecipeResult[];

            // If responseData was empty but the API call succeeded, return an empty array
            return results;
        },
        []
    );

    return { runRecipes };
}
